const SUPPORTED_SCHEMA_VERSIONS = new Set([
    'photone-analysis-api.v1',
    'photone-analysis-api.v2'
]);

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toTrimmedString = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim();
    return normalized || null;
};

const normalizeSeverityLabel = ({ severityLabel, severityCode } = {}) => {
    const normalizedLabel = toTrimmedString(severityLabel);
    if (normalizedLabel) {
        return normalizedLabel;
    }
    const normalizedCode = toTrimmedString(severityCode);
    if (['minor', 'moderate', 'major'].includes(normalizedCode)) {
        return normalizedCode;
    }
    return null;
};

const normalizeEvidenceGroup = (input = null) => {
    if (!isPlainObject(input)) {
        return null;
    }

    return {
        fieldPaths: Array.isArray(input.field_paths)
            ? input.field_paths.map(toTrimmedString).filter(Boolean)
            : (Array.isArray(input.fieldPaths) ? input.fieldPaths.map(toTrimmedString).filter(Boolean) : []),
        sourceSummary: toTrimmedString(input.source_summary || input.sourceSummary),
        referenceSummary: toTrimmedString(input.reference_summary || input.referenceSummary),
        interpretation: toTrimmedString(input.interpretation)
    };
};

const normalizeKeyGap = (input = null) => {
    if (!isPlainObject(input)) {
        return null;
    }

    const severityCode = toTrimmedString(input.severity_code || input.severityCode);
    const severityLabel = normalizeSeverityLabel({
        severityLabel: input.severity_label || input.severityLabel,
        severityCode
    });

    return {
        gapId: toTrimmedString(input.gap_id || input.gapId || input.id),
        title: toTrimmedString(input.title),
        brief: toTrimmedString(input.brief),
        severityCode,
        severityLabel,
        severityReason: toTrimmedString(input.severity_reason || input.severityReason)
    };
};

const normalizeGap = (input = null) => {
    if (!isPlainObject(input)) {
        return null;
    }

    const severityCode = toTrimmedString(input.severity_code || input.severityCode);
    const severityLabel = normalizeSeverityLabel({
        severityLabel: input.severity_label || input.severityLabel,
        severityCode
    });
    const evidenceGroups = Array.isArray(input.evidence_groups)
        ? input.evidence_groups
        : (Array.isArray(input.evidenceGroups) ? input.evidenceGroups : []);

    return {
        id: toTrimmedString(input.id || input.gap_id || input.gapId),
        title: toTrimmedString(input.title),
        brief: toTrimmedString(input.brief),
        descriptionMd: toTrimmedString(input.description_md || input.descriptionMd),
        severityCode,
        severityLabel,
        severityReason: toTrimmedString(input.severity_reason || input.severityReason),
        evidenceGroups: evidenceGroups.map(normalizeEvidenceGroup).filter(Boolean)
    };
};

export function normalizeAnalysisPayload(input = null) {
    if (!isPlainObject(input)) {
        return null;
    }

    const schemaVersion = toTrimmedString(input.schema_version || input.schemaVersion);
    if (!SUPPORTED_SCHEMA_VERSIONS.has(schemaVersion)) {
        return null;
    }

    const overview = isPlainObject(input.overview) ? input.overview : {};
    const keyGaps = Array.isArray(input.key_gaps)
        ? input.key_gaps
        : (Array.isArray(input.keyGaps) ? input.keyGaps : []);
    const gaps = Array.isArray(input.gaps) ? input.gaps : [];

    const normalizedGaps = gaps.map(normalizeGap).filter(Boolean);
    const gapById = new Map(
        normalizedGaps
            .filter(gap => gap.id)
            .map(gap => [gap.id, gap])
    );

    return {
        schemaVersion,
        keyGaps: keyGaps.map(normalizeKeyGap).filter(Boolean).map((gap) => ({
            ...gap,
            severityReason: gap.severityReason || gapById.get(gap.gapId)?.severityReason || null
        })),
        imageDiagnosisMd: toTrimmedString(input.image_diagnosis_md || input.imageDiagnosisMd),
        evidenceReadingMd: toTrimmedString(input.evidence_reading_md || input.evidenceReadingMd),
        overview: {
            summaryMd: toTrimmedString(overview.summary_md || overview.summaryMd),
            postProcessingSuggestionsMd: toTrimmedString(
                overview.post_processing_suggestions_md || overview.postProcessingSuggestionsMd
            )
        },
        gaps: normalizedGaps
    };
}

export default {
    normalizeAnalysisPayload
};
